import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { Box, Typography, IconButton, Button } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useTranslation } from "react-i18next";
import { apiGetRequest } from "../../../../Helpers/Requests";
import { useSnackbar } from "../../../../Components/SnackBar";
import endpoints from "../../../../Helpers/Endpoints";
import { getCurrencySymbol } from "../../../../Utils/CountriesData/getCountriesData";
import { formatPrice } from "../../../../Helpers/formating";
import { categoryNamespaces } from "../../../../Helpers/CategoryNamespaces";
import {
  useOrderMinimum,
  isBelowOrderMinimum,
  memberPaidValue,
} from "../../../../Constants/orderMinimum";
import ServiceLineCard from "./ServiceLineCard";
import ProductLineCard from "./ProductLineCard";
import {
  LINE_KINDS,
  seedLinesFromJob,
  emptyServiceLine,
  emptyProductLine,
  emptyVariant,
  buildUpdatePayload,
  buildValidationSchema,
} from "./helpers";
import {
  editOrderContainerStyle,
  editSubtitleStyle,
  visitRowStyle,
  lineCardStyle,
  cardTrashButtonStyle,
  addLinkStyle,
} from "./styles";

// The editor only offers the store catalog (endpoints.getProductCategories), so
// the namespace chain is fixed to that vertical rather than derived per render.
const PRODUCT_CATEGORY_NS = categoryNamespaces("products");

// COMY1-292 — supplier "Edit Order" editor. Replaces the read panel in-place.
// The footer's "Update & Notify Client" button submits this form via the shared
// form id; `onSubmit` receives the built API payload.
const EditOrder = ({ jobData, formId, onSubmit, onBack, onBelowMinimumChange }) => {
  const { t, i18n } = useTranslation("view-job-modal");
  const isHebrew = i18n.language === "he";
  const backArrowIcon = isHebrew
    ? "/right_arrow_icon.svg"
    : "/left_arrow_icon.svg";
  // The "New Product" row picks from the STORE catalog tree, which has its own
  // namespace — the shared `categories` one only covers service categories.
  const { t: tCat } = useTranslation(PRODUCT_CATEGORY_NS);
  const { t: tServices } = useTranslation("services");
  const { t: tSku } = useTranslation("sku");
  const { t: tUnits } = useTranslation("units");

  const currencySymbol = getCurrencySymbol(jobData?.order_summary?.currency);

  // A visit-originated order carries a visit fee (shown read-only below) and is
  // paid via the visit, so it may be emptied entirely; a real service/product
  // order must keep at least one line. COMY1-292.
  const isVisitOrder = !!jobData?.order_summary?.is_visit_order;
  const visitPrice = jobData?.order_summary?.visit_price ?? null;
  const validationSchema = useMemo(
    () => buildValidationSchema(t, { allowEmpty: isVisitOrder }),
    [t, isVisitOrder]
  );

  const formik = useFormik({
    initialValues: {
      lines: seedLinesFromJob(jobData, {
        service: tServices,
        sku: tSku,
        unit: tUnits,
      }),
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(buildUpdatePayload(values.lines));
    },
  });

  const lines = formik.values.lines;

  const snackbar = useSnackbar();

  // An edited order still has to be worth charging for. The supplier works in
  // list prices while the member pays after the community discount, so the
  // edited total is converted with the discount this order already carries,
  // and compared against the house minimum.
  const orderMinimum = useOrderMinimum();
  const editedGross = useMemo(() => {
    const linesTotal = (lines || []).reduce((sum, line) => {
      if (line.kind === LINE_KINDS.SERVICE) {
        return (
          sum +
          (line.variants || []).reduce(
            (variantSum, variant) =>
              variantSum +
              (Number(variant.amount) || 0) * (Number(variant.unit_price) || 0),
            0,
          )
        );
      }
      return (
        sum +
        (Number(line.quantity) || 0) *
          (Number(line.unit_price || line.product_price) || 0)
      );
    }, 0);
    return linesTotal + (Number(visitPrice) || 0);
  }, [lines, visitPrice]);

  const belowMinimum = isBelowOrderMinimum(
    memberPaidValue(editedGross, jobData?.order_summary),
    orderMinimum,
  );

  useEffect(() => {
    onBelowMinimumChange?.(belowMinimum, orderMinimum);
  }, [belowMinimum, orderMinimum, onBelowMinimumChange]);

  // A failed save can have its field errors scrolled out of view, so the click
  // looks like a silent no-op — surface a summary snackbar. Driven off the
  // actual validation result (not the stale `formik.isValid`), so a valid save
  // never shows a false error. `handleSubmit` still runs the normal flow:
  // touches fields, shows per-field errors, and calls onSubmit only when valid.
  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Refused here too, not only by the disabled button — the member must
    // never be sent an order they are not allowed to pay for.
    if (belowMinimum) {
      snackbar(t("below_minimum_after_edit", { amount: orderMinimum }), "error");
      return;
    }
    formik.handleSubmit(e);
    formik.validateForm().then((errs) => {
      if (Object.keys(errs).length > 0) {
        // Prefer the array-level message (e.g. "keep at least one service")
        // when present; otherwise a generic "fill the highlighted fields".
        const arrayMsg =
          typeof errs.lines === "string" ? errs.lines : null;
        snackbar(arrayMsg || t("edit.errors.form_invalid"), "error");
      }
    });
  };

  // --- Option data sources -------------------------------------------------
  const [services, setServices] = useState([]); // [{ id, label }]
  const [productOptions, setProductOptions] = useState([]); // [{ id, name, price, category_id }]
  const [categoryOptions, setCategoryOptions] = useState([]); // flattened
  const [variantsByService, setVariantsByService] = useState({});
  const [unitOptions, setUnitOptions] = useState([]); // [{ id, label }]
  // Options load in the background (N+1 pricelist calls); this drives a loading
  // indicator inside the dropdowns rather than showing an empty list.
  const [servicesLoading, setServicesLoading] = useState(true);

  // Supplier's real services (the "Choose A Service" list) + their variants, in
  // ONE call (COMY1-292: replaces the old pricelists + per-subcategory N+1).
  // Each service already carries all its variants (price null ⇒ "not listed").
  useEffect(() => {
    (async () => {
      try {
        // Scope services to the order's category so an edit can't re-label the
        // order (COMY1-292).
        const res = await apiGetRequest(
          endpoints.supplierServicesForEditor(jobData?.order_category_id)
        );
        const svcList = res?.data?.services || [];

        const serviceOptions = [];
        const variantMap = {}; // service_id -> [variant option]
        const unitMap = {}; // unit_id -> { id, label }

        svcList.forEach((svc) => {
          const key = String(svc.id);
          serviceOptions.push({
            id: svc.id,
            label: tServices(svc.name, svc.name),
          });
          variantMap[key] = (svc.variants || []).map((v) => {
            const unitLabel = v.unit_name ? tUnits(v.unit_name, v.unit_name) : "";
            const unitId = v.unit_id ?? "";
            if (unitId !== "" && !unitMap[unitId]) {
              unitMap[unitId] = {
                id: unitId,
                label: unitLabel || String(unitId),
              };
            }
            return {
              variant_id: v.variant_id,
              label: tSku(v.sku, v.sku),
              unit_id: unitId,
              unit_label: unitLabel,
              price: v.price ?? null,
              is_not_listed: v.is_not_listed === true || v.price == null,
            };
          });
        });

        setServices(serviceOptions);
        setVariantsByService(variantMap);
        setUnitOptions(Object.values(unitMap));
      } catch (e) {
        console.error("Edit order: failed to load services", e);
      } finally {
        setServicesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Supplier's existing products (the "Choose Product" list).
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGetRequest(
          `${endpoints.supplierProducts}?page=1&page_size=200`
        );
        const products = res?.data?.products || [];
        setProductOptions(
          products.map((p) => ({
            id: p.id,
            name: p.name,
            price: p.price,
            category_id: p.category_id,
          }))
        );
      } catch (e) {
        console.error("Edit order: failed to load products", e);
      }
    })();
  }, []);

  // Product categories (New Product), flattened parent > child like ProductModal.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGetRequest(endpoints.getProductCategories);
        const cats = res?.data?.categories || [];
        const flat = [];
        cats.forEach((cat) => {
          flat.push({
            id: cat.id,
            name: tCat(cat.name, cat.name),
            isParent: true,
          });
          (cat.subcategories || []).forEach((sub) => {
            flat.push({
              id: sub.id,
              name: tCat(sub.name, sub.name),
              label: `${tCat(cat.name, cat.name)} > ${tCat(sub.name, sub.name)}`,
              isParent: false,
            });
          });
        });
        setCategoryOptions(flat);
      } catch (e) {
        console.error("Edit order: failed to load product categories", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Variants for every service are loaded up front (see effect above) and cached
  // in `variantsByService` keyed by service id, so there is nothing to lazily
  // fetch when a service is chosen.
  const loadVariants = () => {};

  // --- Formik line mutators ------------------------------------------------
  const setLineField = (i, field, value) =>
    formik.setFieldValue(`lines.${i}.${field}`, value);

  const setVariantField = (i, vIndex, field, value) =>
    formik.setFieldValue(`lines.${i}.variants.${vIndex}.${field}`, value);

  // Rows added AFTER a failed save are stamped with the current submit count,
  // so their errors only show once the supplier tries to save again (a fresh
  // row shouldn't light up red the moment it's added).
  const addVariant = (i) =>
    formik.setFieldValue(`lines.${i}.variants`, [
      ...lines[i].variants,
      { ...emptyVariant(), created_at_submit: formik.submitCount },
    ]);

  const removeVariant = (i, vIndex) =>
    formik.setFieldValue(
      `lines.${i}.variants`,
      lines[i].variants.filter((_, idx) => idx !== vIndex)
    );

  const changeKind = (i, kind) => {
    const { uid, created_at_submit } = lines[i];
    const base =
      kind === LINE_KINDS.PRODUCT ? emptyProductLine() : emptyServiceLine();
    formik.setFieldValue(`lines.${i}`, { ...base, uid, created_at_submit });
  };

  const addLine = () =>
    formik.setFieldValue("lines", [
      ...lines,
      { ...emptyServiceLine(), created_at_submit: formik.submitCount },
    ]);

  const removeLine = (i) =>
    formik.setFieldValue(
      "lines",
      lines.filter((_, idx) => idx !== i)
    );

  return (
    <Box className="custom-scrollbar" sx={editOrderContainerStyle}>
      {/* Top row: "back to order details without saving" + subtitle (Figma:
          this lives inside the editor panel, NOT in the modal header). */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Box
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            width: "max-content",
          }}
        >
          <img src={backArrowIcon} alt="back" />
          <Typography
            sx={{
              color: "rgba(72, 72, 72, 1)",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {t("edit.back_to_order")}
          </Typography>
        </Box>
        <Typography sx={editSubtitleStyle}>{t("edit.subtitle")}</Typography>
      </Box>

      {/* Visit fee (COMY1-292): read-only, always part of the total — the
          supplier can't edit or remove it. Shown for visit-originated orders. */}
      {isVisitOrder && (
        <Box sx={visitRowStyle}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "rgba(47, 43, 61, 0.9)",
            }}
          >
            {t("Visit Price")}
          </Typography>
          <Typography
            dir="ltr"
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "rgba(47, 43, 61, 0.9)",
            }}
          >
            {visitPrice != null
              ? `${currencySymbol}${formatPrice(visitPrice)}`
              : "-"}
          </Typography>
        </Box>
      )}

      <form id={formId} onSubmit={handleFormSubmit}>
        <Box display="flex" flexDirection="column" gap="16px">
          {lines.map((line, i) => {
            const lineErrors = formik.errors.lines?.[i];
            // A line added after the last failed save shows its errors only
            // once the supplier tries to save again.
            const lineShowErrors =
              formik.submitCount > (line.created_at_submit || 0);
            return (
              <Box key={line.uid} sx={lineCardStyle}>
                <IconButton
                  size="small"
                  onClick={() => removeLine(i)}
                  // A service/product order can't be emptied — keep the last
                  // remaining line. A visit order may be emptied. COMY1-292.
                  disabled={!isVisitOrder && lines.length === 1}
                  sx={cardTrashButtonStyle}
                  aria-label={t("edit.remove_line")}
                >
                  {line.kind === LINE_KINDS.PRODUCT ? (
                    <RemoveCircleOutlineIcon fontSize="small" />
                  ) : (
                    <img src="/icons/delete-icon.svg" alt="" width="18" />
                  )}
                </IconButton>

                {line.kind === LINE_KINDS.SERVICE ? (
                  <ServiceLineCard
                    index={i}
                    line={line}
                    services={services}
                    servicesLoading={servicesLoading}
                    variantOptions={variantsByService[String(line.service_id)]}
                    unitOptions={unitOptions}
                    loadVariants={loadVariants}
                    currencySymbol={currencySymbol}
                    changeKind={changeKind}
                    setLineField={setLineField}
                    setVariantField={setVariantField}
                    addVariant={addVariant}
                    removeVariant={removeVariant}
                    errors={lineErrors}
                    showErrors={lineShowErrors}
                    submitCount={formik.submitCount}
                  />
                ) : (
                  <ProductLineCard
                    index={i}
                    line={line}
                    productOptions={productOptions}
                    categoryOptions={categoryOptions}
                    currencySymbol={currencySymbol}
                    changeKind={changeKind}
                    setLineField={setLineField}
                    errors={lineErrors}
                    showErrors={lineShowErrors}
                  />
                )}
              </Box>
            );
          })}

          <Button
            variant="text"
            startIcon={<AddCircleOutlineIcon fontSize="small" />}
            onClick={addLine}
            sx={addLinkStyle}
            disableRipple
          >
            {t("edit.add_line")}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditOrder;
